import React from "react";
import { Box, Button, Alert, Typography, Tooltip, FormControlLabel, Checkbox } from "@mui/material";
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker/DateTimePicker';
import { InfoOutlined } from '@mui/icons-material';

type Props = {
  editing: boolean;
  isRegistrationCompleted: boolean;
  isLimitExceeded: boolean;
  isScheduled: boolean;
  toggleScheduled: () => void;
  selectedDate: Date | null | undefined;
  setSelectedDate: (d: Date | null) => void;
  minDate: Date;
  maxDate: Date;
  disableDateSelect: (date: Date) => boolean;
  sendNowDisabled: boolean;
  handleSendNow: () => void;
  buttonInfoText: string;
  buttonInfoClick: (() => void) | null;
  scheduledDate: string | undefined;
  wasSent: boolean;
};

export default function CampaignTextActions({
  editing,
  isRegistrationCompleted,
  isLimitExceeded,
  isScheduled,
  toggleScheduled,
  selectedDate,
  setSelectedDate,
  minDate,
  maxDate,
  disableDateSelect,
  sendNowDisabled,
  handleSendNow,
  buttonInfoText,
  buttonInfoClick,
  scheduledDate,
  wasSent
}: Props) {
  if (editing) {
    return (
      <>
        <FormControlLabel
          disabled={!isRegistrationCompleted || isLimitExceeded}
          sx={styles.scheduleCheckbox}
          control={
            <Checkbox
              checked={isScheduled}
              onChange={toggleScheduled}
              color="primary"
            />
          }
          label="Schedule send"
        />
        {isScheduled && (
          <DateTimePicker
            label="Select date and time"
            value={selectedDate}
            minDate={minDate}
            maxDate={maxDate}
            shouldDisableDate={disableDateSelect}
            onChange={setSelectedDate}
            slotProps={{
              textField: {
                size: 'small',
                sx: styles.dateTimePicker
              }
            }}
          />
        )}
        <Box sx={styles.flexSpacer} />
        <Button
          variant="contained"
          color="primary"
          onClick={handleSendNow}
          disabled={sendNowDisabled}
        >
          Send Now
        </Button>
      </>
    );
  }

  return (
    <Box sx={styles.nonEditingActions}>
      {!scheduledDate && !wasSent && (
        <Alert
          severity="info"
          variant="filled"
          sx={styles.stepAlert}
        >
          <Typography>
            <Box component="span" sx={{ fontWeight: 'bold' }}>STEP 3:</Box>
            <Box component="span" sx={{ ml: 0.5 }}>Send your message now or schedule it</Box>
          </Typography>
        </Alert>
      )}
      <Box sx={styles.buttonGroup}>
        {buttonInfoText && (
          <Tooltip
            onClick={(event: React.MouseEvent<HTMLDivElement>) => buttonInfoClick?.()}
            title={buttonInfoText}
          >
            <InfoOutlined sx={{ cursor: buttonInfoClick ? 'pointer' : 'default', mr: 1 }} />
          </Tooltip>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSendNow}
          disabled={sendNowDisabled}
        >
          Send Now
        </Button>
      </Box>
    </Box>
  );
}

const styles = {
  scheduleCheckbox: {
    marginRight: '16px',
  },
  dateTimePicker: {
    minWidth: '200px',
    maxWidth: '220px',
    '& .MuiInputBase-root': {
      fontSize: '0.875rem',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
    }
  },
  flexSpacer: {
    flex: 1
  },
  nonEditingActions: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: '8px',
    justifyContent: 'flex-end',
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  stepAlert: {
    flex: 1,
    mr: 2,
    maxWidth: 'initial',
    marginLeft: 'initial',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
};
